import type { DeskGuest, DeskGuestPage } from "@seagames/contracts";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { api } from "../../lib/api/client";

/**
 * What a HOTEL login reads beyond S4's desk reads (`deskGroupsOptions`,
 * `deskPeopleOptions`, `checkAssignments` in `lib/api/accommodation.ts`).
 *
 * A guest as a hotel may see it (`DeskGuest`, PLAN §0b Q3): name, AC number,
 * organisation, gender, category, room, stay, the window, the photo thumb.
 * NEVER the ID number, date of birth, phone or documents — the contract has
 * no field for them and nothing here asks. Guests drop off its lists 3 days
 * after check-out (`HOTEL_LIST_RETENTION_DAYS`, in the API's predicate).
 */
export type HotelGuest = DeskGuest;

export interface InHouseFilter {
	games_id: string;
	/** Comma list; absent = every building the login runs. */
	lodging_building_ids?: string;
	q?: string;
	page: number;
	page_size: number;
}

const clean = <T extends object>(o: T): T => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== "")) as T;

/**
 * `GET /accommodation/in-house` (`inHouseQuery`, S3) → `Paginated<DeskGuest>` — checked in, not out, in the
 * login's buildings; offset (one building is bounded). Under S4's
 * `["accommodation", …]` prefix, so a check-in / out refreshes it.
 */
export const inHouseOptions = (f: InHouseFilter) =>
	queryOptions({
		queryKey: ["accommodation", "in-house", clean(f)] as const,
		queryFn: ({ signal }) => api.get<DeskGuestPage>("/accommodation/in-house", { query: { ...clean(f) }, signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
		refetchInterval: 60_000,
	});
